/**
 * sales-boost.js — Livostyle sales conversion toolkit
 * Modules: wishlist, sticky ATC, recently viewed, free-ship bar,
 *          size guide modal, cart add-ons, bundle tiers, delivery estimate.
 * v3 (2026-10): removed the end-of-day countdown (fake urgency), size guide uses
 * delegated listeners (survives variant re-renders), add-ons re-render the cart
 * drawer via the Section Rendering API, honest delivery-estimate module added.
 * Deferred-loaded (scripts are `defer` in theme.liquid).
 * No external deps. Vanilla ES2017+.
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------
   * 0. Shared utilities
   * --------------------------------------------------------- */
  var LS = window.localStorage;
  var hasLS = (function () {
    try { LS.setItem('__t', '1'); LS.removeItem('__t'); return true; }
    catch (e) { return false; }
  })();
  function readLS(key, fallback) {
    if (!hasLS) return fallback;
    try { var v = LS.getItem(key); return v ? JSON.parse(v) : fallback; }
    catch (e) { return fallback; }
  }
  function writeLS(key, value) {
    if (!hasLS) return;
    try { LS.setItem(key, JSON.stringify(value)); } catch (e) {}
  }
  function formatMoney(cents, currency) {
    // Works with Shopify money cents. Currency defaults to USD.
    var code = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }
  function fireEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {}, bubbles: true }));
  }

  /* -----------------------------------------------------------
   * 1. Wishlist module
   * --------------------------------------------------------- */
  var WL_KEY = 'livostyle_wishlist';
  var Wishlist = {
    get: function () { return readLS(WL_KEY, []); },
    has: function (handle) { return this.get().indexOf(handle) !== -1; },
    add: function (handle) {
      if (!handle) return;
      var list = this.get();
      if (list.indexOf(handle) === -1) { list.unshift(handle); writeLS(WL_KEY, list); }
      fireEvent('wishlist:change', { handle: handle, action: 'add', count: list.length });
    },
    remove: function (handle) {
      var list = this.get().filter(function (h) { return h !== handle; });
      writeLS(WL_KEY, list);
      fireEvent('wishlist:change', { handle: handle, action: 'remove', count: list.length });
    },
    toggle: function (handle) {
      if (this.has(handle)) this.remove(handle);
      else this.add(handle);
      return this.has(handle);
    },
    count: function () { return this.get().length; }
  };
  window.LivostyleWishlist = Wishlist;

  function bindWishlistButtons(scope) {
    var root = scope || document;
    var buttons = root.querySelectorAll('[data-wishlist-btn]');
    buttons.forEach(function (btn) {
      if (btn.__wlBound) return;
      btn.__wlBound = true;
      var handle = btn.getAttribute('data-wishlist-handle');
      if (Wishlist.has(handle)) btn.classList.add('is-active');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var active = Wishlist.toggle(handle);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active) btn.classList.add('sb-burst');
        setTimeout(function () { btn.classList.remove('sb-burst'); }, 600);
      });
    });
  }

  function updateWishlistBadge() {
    var n = Wishlist.count();
    // Heart buttons / per-card counters (integer only)
    document.querySelectorAll('[data-wishlist-count]:not([data-wishlist-page] [data-wishlist-count])').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('is-visible', n > 0);
    });
    // Header bubble (visibility toggle + numeric)
    document.querySelectorAll('[data-wishlist-header-count]').forEach(function (el) {
      var inner = el.querySelector('span[aria-hidden="true"]') || el;
      inner.textContent = n > 99 ? '99+' : n;
      if (n > 0) { el.removeAttribute('hidden'); }
      else { el.setAttribute('hidden', ''); }
    });
  }

  document.addEventListener('wishlist:change', updateWishlistBadge);

  /* -----------------------------------------------------------
   * 2. Recently viewed tracker
   * --------------------------------------------------------- */
  var RV_KEY = 'livostyle_recently_viewed';
  var RV_MAX = 12;
  var RecentlyViewed = {
    get: function () { return readLS(RV_KEY, []); },
    add: function (handle) {
      if (!handle) return;
      var list = this.get().filter(function (h) { return h !== handle; });
      list.unshift(handle);
      if (list.length > RV_MAX) list = list.slice(0, RV_MAX);
      writeLS(RV_KEY, list);
    }
  };
  window.LivostyleRecentlyViewed = RecentlyViewed;

  // Auto-track on PDP via meta tag
  var pdpMeta = document.querySelector('meta[name="product-handle"]');
  if (pdpMeta) RecentlyViewed.add(pdpMeta.getAttribute('content'));

  /* -----------------------------------------------------------
   * 3. Free-ship progress bar
   * --------------------------------------------------------- */
  var FREE_SHIP_THRESHOLD_CENTS = (function () {
    var meta = document.querySelector('meta[name="livostyle-freeship-threshold"]');
    return meta ? parseInt(meta.getAttribute('content'), 10) : 9700;
  })();
  window.LivostyleFreeShipThreshold = FREE_SHIP_THRESHOLD_CENTS;

  function renderFreeShipBar(cartTotalCents) {
    var bars = document.querySelectorAll('[data-freeship-bar]');
    if (!bars.length) return;
    var total = Math.max(0, cartTotalCents || 0);
    var remaining = Math.max(0, FREE_SHIP_THRESHOLD_CENTS - total);
    var pct = Math.min(100, Math.round((total / FREE_SHIP_THRESHOLD_CENTS) * 100));

    bars.forEach(function (bar) {
      var fill = bar.querySelector('[data-freeship-fill]');
      var msg = bar.querySelector('[data-freeship-msg]');
      if (fill) fill.style.width = pct + '%';
      if (msg) {
        if (remaining === 0) {
          bar.classList.add('is-complete');
          msg.innerHTML = 'You’ve unlocked <strong>free US shipping</strong>';
        } else {
          bar.classList.remove('is-complete');
          msg.innerHTML = 'You’re <strong>' + formatMoney(remaining) + '</strong> away from <strong>free US shipping</strong>';
        }
      }
    });
  }
  window.LivostyleRenderFreeShipBar = renderFreeShipBar;

  function refreshCartUI() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderFreeShipBar(cart.total_price);
        fireEvent('cart:updated', { cart: cart });
      })
      .catch(function () {});
  }
  window.LivostyleRefreshCart = refreshCartUI;

  /* -----------------------------------------------------------
   * 4. Sticky mobile ATC
   * --------------------------------------------------------- */
  function initStickyATC() {
    var sticky = document.querySelector('[data-sticky-atc]');
    if (!sticky) return;
    var mainCta = document.querySelector('product-form button[type="submit"], .product-form__buttons button[type="submit"]');
    if (!mainCta) { sticky.hidden = true; return; }

    // Show sticky only when main CTA is out of view
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting || entry.boundingClientRect.top > 0) {
          sticky.classList.remove('is-visible');
        } else {
          sticky.classList.add('is-visible');
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px -40% 0px' });
    io.observe(mainCta);

    // Mirror click to main CTA
    var stickyBtn = sticky.querySelector('[data-sticky-atc-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (mainCta.disabled) return;
        mainCta.click();
      });
    }

    // Mirror variant state (disabled / sold out text)
    var sync = function () {
      if (!stickyBtn) return;
      stickyBtn.disabled = !!mainCta.disabled;
      var priceEl = document.querySelector('.price__container .price-item--sale, .price__container .price-item--regular');
      var priceSticky = sticky.querySelector('[data-sticky-atc-price]');
      if (priceSticky && priceEl) priceSticky.textContent = priceEl.textContent.trim();
    };
    sync();
    var mo = new MutationObserver(sync);
    mo.observe(mainCta, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    document.addEventListener('variant:change', sync);
  }

  /* -----------------------------------------------------------
   * 5. Size guide modal
   * --------------------------------------------------------- */
  function initSizeGuideModal() {
    var lastFocus = null;
    function getModal() { return document.querySelector('[data-size-guide-modal]'); }
    function onEsc(e) { if (e.key === 'Escape') close(); }
    function open(e) {
      var modal = getModal();
      if (e) e.preventDefault();
      if (!modal) { window.open('/pages/size-fit-guide', '_blank', 'noopener'); return; }
      lastFocus = document.activeElement;
      modal.hidden = false;
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
      var firstBtn = modal.querySelector('[data-size-guide-close]');
      if (firstBtn) firstBtn.focus();
      document.addEventListener('keydown', onEsc);
    }
    function close() {
      var modal = getModal();
      if (!modal) return;
      modal.classList.remove('is-open');
      setTimeout(function () { modal.hidden = true; }, 220);
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onEsc);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    // Delegated: the size-guide button lives inside <variant-selects>, which Dawn re-renders on every option change.
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-size-guide-open]');
      if (opener) { open(e); return; }
      var modal = getModal();
      if (!modal || modal.hidden) return;
      if (e.target.closest('[data-size-guide-close]') || e.target === modal) close();
    });
  }

  /* -----------------------------------------------------------
   * 6. Bundle tier selector (for PDP custom block)
   * --------------------------------------------------------- */
  function initBundleTiers() {
    var wrap = document.querySelector('[data-bundle-tiers]');
    if (!wrap) return;
    var tiers = wrap.querySelectorAll('[data-bundle-tier]');
    var qtyInput = document.querySelector('product-form .quantity__input, input[name="quantity"]');
    tiers.forEach(function (tier) {
      tier.addEventListener('click', function () {
        var qty = parseInt(tier.getAttribute('data-bundle-tier'), 10) || 1;
        tiers.forEach(function (t) { t.classList.toggle('is-selected', t === tier); });
        if (qtyInput) {
          qtyInput.value = qty;
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* -----------------------------------------------------------
   * 7. Cart upsell — quick-add from drawer
   * --------------------------------------------------------- */
  function initCartUpsell() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-upsell-add]');
      if (!btn) return;
      e.preventDefault();
      var item = btn.closest('.sb-cart-upsell__item');
      var input = item && item.querySelector('[data-upsell-variant-input]');
      var vid = input && input.value;
      if (!vid) return;
      btn.disabled = true;
      btn.classList.add('is-loading');
      var drawer = document.querySelector('cart-drawer');
      var onCartPage = /^\/cart\/?$/.test(window.location.pathname);
      var body = { items: [{ id: parseInt(vid, 10), quantity: 1 }] };
      if (drawer && !onCartPage) {
        body.sections = 'cart-drawer,cart-icon-bubble';
        body.sections_url = window.location.pathname;
      }
      fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.status) { throw new Error(res.description || res.message || 'Error'); }
          if (onCartPage || !drawer) { window.location.reload(); return; }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'livo-addons', productVariantId: vid, cartData: res });
          }
          if (res.sections && typeof drawer.renderContents === 'function') {
            drawer.renderContents({ id: res.items && res.items[0] ? res.items[0].id : vid, sections: res.sections });
          }
        })
        .catch(function () {
          btn.textContent = 'Try again';
          btn.disabled = false;
          btn.classList.remove('is-loading');
        });
    });
  }

  /* -----------------------------------------------------------
   * 8. Delivery estimate (PDP) — honest range, weekends skipped
   *    Reads data-proc-min/max and data-transit-min/max (business days).
   * --------------------------------------------------------- */
  function addBusinessDays(date, days) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      var wd = d.getDay();
      if (wd !== 0 && wd !== 6) added++;
    }
    return d;
  }
  function initDeliveryEstimate() {
    var els = document.querySelectorAll('[data-livo-eta]');
    if (!els.length) return;
    var fmt;
    try { fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }); } catch (e) { fmt = null; }
    var today = new Date();
    els.forEach(function (el) {
      var pMin = parseInt(el.getAttribute('data-proc-min'), 10) || 1;
      var pMax = parseInt(el.getAttribute('data-proc-max'), 10) || 3;
      var tMin = parseInt(el.getAttribute('data-transit-min'), 10) || 2;
      var tMax = parseInt(el.getAttribute('data-transit-max'), 10) || 10;
      var from = addBusinessDays(today, pMin + tMin);
      var to = addBusinessDays(today, pMax + tMax);
      var out = el.querySelector('[data-livo-eta-range]');
      if (out && fmt) out.textContent = fmt.format(from) + ' – ' + fmt.format(to);
    });
  }

  /* -----------------------------------------------------------
   * 9. Init
   * --------------------------------------------------------- */
  function init() {
    bindWishlistButtons();
    updateWishlistBadge();
    initStickyATC();
    initSizeGuideModal();
    initBundleTiers();
    initCartUpsell();
    initDeliveryEstimate();
    refreshCartUI();

    // Re-bind wishlist on dynamic DOM changes (cart drawer, quick add, etc.)
    document.addEventListener('cart:updated', function () {
      bindWishlistButtons();
      updateWishlistBadge();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
